<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import { useStoreGetters, useMapGetter } from 'dashboard/composables/store';
import { audienceLabel } from 'dashboard/helper/campaignAudience';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CampaignLayout from 'dashboard/components-next/Campaigns/CampaignLayout.vue';
import CampaignList from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CampaignList.vue';
import ConfirmDeleteCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/ConfirmDeleteCampaignDialog.vue';
import WhatsAppCampaignEmptyState from 'dashboard/components-next/Campaigns/EmptyState/WhatsAppCampaignEmptyState.vue';

const { t } = useI18n();
const getters = useStoreGetters();

const router = useRouter();
const route = useRoute();

const selectedCampaign = ref(null);

// Criar campanha é o wizard, não mais o popover de seis campos. O popover não mostrava o que o
// cliente ia receber, não contava destinatários e não deixava revisar a lista — e a campanha que
// ele criava não materializava conversa, então a resposta do cliente caía num lugar sem histórico.
const irParaNovaCampanha = () =>
  router.push({ name: 'campaigns_new', params: { accountId: route.params.accountId } });

const uiFlags = useMapGetter('campaigns/getUIFlags');
const isFetchingCampaigns = computed(() => uiFlags.value.isFetching);

const confirmDeleteCampaignDialogRef = ref(null);

const labels = useMapGetter('labels/getLabels');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

// O wizard tem flag própria. Sem ela a rota existe mas o guard redireciona calado, então o botão
// não deve nem aparecer — senão o clique parece engolido.
const podeCriarCampanha = computed(() =>
  isFeatureEnabledonAccount.value(route.params.accountId, FEATURE_FLAGS.OUTBOUNDS)
);

// A audiência chega como referência (`Label` traz só o id), e é ela que distingue duas campanhas
// com o mesmo template no mesmo dia. Resolvida aqui, onde o store dos rótulos está à mão — o card
// é burro de propósito.
const WhatsAppCampaigns = computed(() =>
  getters['campaigns/getWhatsAppCampaigns'].value.map(campaign => ({
    ...campaign,
    audienceLabel: audienceLabel(campaign.audience, labels.value),
  }))
);

const hasNoWhatsAppCampaigns = computed(
  () => WhatsAppCampaigns.value?.length === 0 && !isFetchingCampaigns.value
);

const abrirDetalhe = campaign =>
  router.push({
    name: 'campaigns_detail',
    params: { accountId: route.params.accountId, campaignId: campaign.id },
  });

const handleDelete = campaign => {
  selectedCampaign.value = campaign;
  confirmDeleteCampaignDialogRef.value.dialogRef.open();
};
</script>

<template>
  <CampaignLayout
    :header-title="t('CAMPAIGN.WHATSAPP.HEADER_TITLE')"
    :button-label="podeCriarCampanha ? t('CAMPAIGN.WHATSAPP.NEW_CAMPAIGN') : ''"
    @click="irParaNovaCampanha"
  >
    <div
      v-if="isFetchingCampaigns"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>
    <CampaignList
      v-else-if="!hasNoWhatsAppCampaigns"
      :campaigns="WhatsAppCampaigns"
      is-whats-app-type
      @open="abrirDetalhe"
      @delete="handleDelete"
    />
    <WhatsAppCampaignEmptyState
      v-else
      :title="t('CAMPAIGN.WHATSAPP.EMPTY_STATE.TITLE')"
      :subtitle="t('CAMPAIGN.WHATSAPP.EMPTY_STATE.SUBTITLE')"
      class="pt-14"
    />
    <ConfirmDeleteCampaignDialog
      ref="confirmDeleteCampaignDialogRef"
      :selected-campaign="selectedCampaign"
    />
  </CampaignLayout>
</template>
